import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, label, error, hint, id, children, ...rest },
  ref
) {
  const selectId = id || `s-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-slate-700 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          ref={ref}
          aria-invalid={!!error}
          className={cn(
            'block w-full appearance-none rounded-md border bg-white text-sm text-slate-900',
            'h-10 px-3.5 pr-10 transition-colors',
            'disabled:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70',
            error
              ? 'border-red-300 focus:border-red-500'
              : 'border-slate-200 focus:border-brand-500',
            className
          )}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
        />
      </div>
      {(error || hint) && (
        <p
          className={cn(
            'text-xs mt-1.5',
            error ? 'text-red-600' : 'text-slate-500'
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});
